import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, Download, Columns3, List, Layers, ChevronDown, ChevronRight, Hash, BarChart3, Brain } from 'lucide-react';
import RelevanceBadge from './RelevanceBadge';
import MatchBadges from './MatchBadges';
import Pagination from './Pagination';
import HSCode from '../common/HSCode';
import { getResultCountDisplay } from '../../utils/searchDisplay';

const COMPACT_THRESHOLD = 620;

/**
 * Find longest common prefix among all codes.
 * Returns the number of characters that are shared.
 */
function findCommonPrefix(codes) {
  if (!codes || codes.length < 2) return 0;
  const first = codes[0];
  let prefix = first.length;
  for (let i = 1; i < codes.length; i++) {
    const other = codes[i];
    let j = 0;
    while (j < prefix && j < other.length && first[j] === other[j]) j++;
    prefix = j;
  }
  return prefix;
}

const COLUMNS = [
  { key: 'product_code', label: 'Код', width: 'w-[130px]', sortable: true, defaultVisible: true },
  { key: 'product_description_original', label: 'Описание', width: '', flex: true, sortable: false, defaultVisible: true },
  { key: 'country_of_origin', label: 'Страна', width: 'w-[60px]', sortable: true, defaultVisible: true },
  { key: 'customs_value_usd', label: 'USD', width: 'w-[80px]', sortable: true, numeric: true, defaultVisible: true },
  { key: 'weight_net_kg', label: 'Вес', width: 'w-[60px]', sortable: true, numeric: true, defaultVisible: false },
  { key: 'trademark', label: 'Марка', width: 'w-[100px]', sortable: true, defaultVisible: false },
  { key: 'declaration_year', label: 'Год', width: 'w-[68px]', sortable: true, defaultVisible: true },
  { key: 'importer_name', label: 'Импортёр', width: 'w-[130px]', sortable: false, defaultVisible: false },
  { key: 'relevance', label: 'Совпадение', width: 'w-[100px]', sortable: false, defaultVisible: true },
];

/**
 * Quiet year display. Plain mono number for normal years, amber tint
 * only when the row is "архів" (no year) or pre-2022 (no price_per_kg_usd
 * back-fill — broker should treat the data with caution).
 */
function YearBadge({ year, date }) {
  let y = year;
  if (y == null && date) {
    const m = String(date).match(/^(\d{4})/);
    if (m) y = parseInt(m[1], 10);
  }
  const title = date ? `Дата декларації: ${date}` : (y != null ? `Рік ${y}` : 'Архівні дані без року');
  if (y == null) {
    return (
      <span
        className="inline-flex items-center text-[10px] font-medium px-1 h-[16px] rounded bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300"
        title={title}
      >
        архів
      </span>
    );
  }
  const isOld = y < 2022;
  return (
    <span
      className={`text-[11px] font-mono tabular-nums ${
        isOld ? 'text-amber-700 dark:text-amber-400' : 'text-text-muted'
      }`}
      title={title}
    >
      {y}
    </span>
  );
}

function formatNumber(val) {
  if (val == null || val === '') return '';
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num)) return val;
  return num.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
}

function highlightText(text, query) {
  if (!text || !query) return text;
  const words = query.split(/\s+/).filter(w => w.length > 1);
  if (words.length === 0) return text;
  try {
    const regex = new RegExp(`(${words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part)
        ? <span key={i} className="font-semibold text-text-primary">{part}</span>
        : part
    );
  } catch {
    return text;
  }
}

/** Compact card row for narrow containers */
function CompactRow({ row, isSelected, onRowClick, searchQuery, commonPrefixLen = 0 }) {
  const usd = row.customs_value_usd != null && row.customs_value_usd !== ''
    ? `$${formatNumber(row.customs_value_usd)}`
    : null;
  return (
    <button
      type="button"
      onClick={() => onRowClick && onRowClick(row)}
      className={`w-full text-left flex flex-col gap-1.5 p-2.5 transition-colors border-l-[3px] ${
        isSelected
          ? 'bg-blue-50 dark:bg-blue-950/30 border-l-blue-500 dark:border-l-blue-400'
          : 'hover:bg-hover border-l-transparent'
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <HSCode code={row.product_code} size="sm" commonPrefixLen={commonPrefixLen} />
        <YearBadge year={row.declaration_year} date={row.declaration_date} />
        {row.country_of_origin && (
          <span className="text-[10px] text-text-muted font-mono uppercase">{row.country_of_origin}</span>
        )}
        {usd && (
          <span className="ml-auto text-[11px] font-mono tabular-nums text-text-secondary shrink-0">{usd}</span>
        )}
      </div>
      <div className="text-[12px] leading-relaxed text-text-secondary line-clamp-3" title={row.product_description_original}>
        {highlightText(row.product_description_original, searchQuery)}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <RelevanceBadge score={row.relevance_score} methods={row.search_method} rerankerScore={row.reranker_score} />
        <MatchBadges searchMethod={row.search_method} searchQuery={searchQuery} row={row} />
      </div>
    </button>
  );
}

/** Render a single data row */
function DataRow({ row, activeCols, isSelected, onRowClick, searchQuery }) {
  return (
    <tr
      onClick={() => onRowClick && onRowClick(row)}
      className={`
        cursor-pointer transition-colors
        ${isSelected
          ? 'bg-blue-50 dark:bg-blue-950/30 border-l-[3px] border-l-blue-500 dark:border-l-blue-400'
          : 'hover:bg-hover border-l-[3px] border-l-transparent'
        }
      `}
    >
      {activeCols.map(col => {
        return (
          <td key={col.key} className="px-2 py-2 text-xs">
            {col.key === 'product_description_original' ? (
              <div>
                <div className="line-clamp-2 leading-relaxed text-text-secondary" title={row[col.key]}>
                  {highlightText(row[col.key], searchQuery)}
                </div>
                <MatchBadges
                  searchMethod={row.search_method}
                  searchQuery={searchQuery}
                  row={row}
                />
              </div>
            ) : col.key === 'relevance' ? (
              <RelevanceBadge score={row.relevance_score} methods={row.search_method} rerankerScore={row.reranker_score} />
            ) : col.numeric ? (
              <span className="font-mono text-[11px] text-text-muted">{formatNumber(row[col.key])}</span>
            ) : col.key === 'product_code' ? (
              <HSCode code={row[col.key]} size="sm" />
            ) : col.key === 'importer_name' ? (
              <span className="truncate max-w-[150px] block text-text-muted" title={row[col.key]}>{row[col.key] || ''}</span>
            ) : col.key === 'declaration_year' ? (
              <YearBadge year={row.declaration_year} date={row.declaration_date} />
            ) : (
              <span className="text-text-muted">{row[col.key] != null ? String(row[col.key]) : ''}</span>
            )}
          </td>
        );
      })}
    </tr>
  );
}

export default function ResultsTable({
  results,
  searchInfo,
  totalEstimate,
  currentPage,
  pageSize,
  onPageChange,
  onRowClick,
  onOpenPractice,
  searchQuery,
  selectedId,
}) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [visibleCols, setVisibleCols] = useState(
    () => new Set(COLUMNS.filter(c => c.defaultVisible).map(c => c.key))
  );
  const [showColMenu, setShowColMenu] = useState(false);
  const [groupByCode, setGroupByCode] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [groupSort, setGroupSort] = useState('relevance'); // 'relevance' | 'code' | 'count'
  const rootRef = useRef(null);
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    if (!rootRef.current || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setIsCompact(entry.contentRect.width < COMPACT_THRESHOLD);
      }
    });
    observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortedResults = useMemo(() => {
    if (!sortKey) return results;
    return [...results].sort((a, b) => {
      let va = a[sortKey], vb = b[sortKey];
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      va = String(va);
      vb = String(vb);
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
  }, [results, sortKey, sortDir]);

  // Group results by product_code
  const groupedResults = useMemo(() => {
    if (!groupByCode) return null;
    const groups = {};
    sortedResults.forEach(row => {
      const code = row.product_code || 'Без кода';
      if (!groups[code]) groups[code] = [];
      groups[code].push(row);
    });
    const entries = Object.entries(groups);
    if (groupSort === 'code') {
      entries.sort((a, b) => a[0].localeCompare(b[0]));
    } else if (groupSort === 'count') {
      entries.sort((a, b) => b[1].length - a[1].length);
    } else if (groupSort === 'relevance') {
      const maxScore = (rows) => Math.max(...rows.map(r =>
        r.reranker_score ?? r.relevance_score ?? 0
      ));
      entries.sort((a, b) => maxScore(b[1]) - maxScore(a[1]));
    }
    return entries;
  }, [sortedResults, groupByCode, groupSort]);

  // Auto-expand first group when switching to group mode
  const handleToggleGroup = () => {
    if (!groupByCode) {
      // Switching to group mode — expand first group
      setGroupByCode(true);
      setExpandedGroups(new Set()); // will auto-expand first via render logic
    } else {
      setGroupByCode(false);
    }
  };

  const toggleGroup = (code) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const toggleCol = (key) => {
    setVisibleCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) { if (next.size > 2) next.delete(key); }
      else next.add(key);
      return next;
    });
  };

  const exportCsv = () => {
    const cols = COLUMNS.filter(c => visibleCols.has(c.key) && c.key !== 'relevance');
    const header = cols.map(c => c.label).join(',');
    const rows = sortedResults.map(r =>
      cols.map(c => {
        const v = r[c.key];
        if (v == null) return '';
        const s = String(v);
        return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customs_search_results.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const activeCols = COLUMNS.filter(c => visibleCols.has(c.key));
  // In grouped mode, hide the code column (it's shown in group headers)
  const displayCols = groupByCode
    ? activeCols.filter(c => c.key !== 'product_code')
    : activeCols;
  // Unique code count for the toggle badge
  const uniqueCodeCount = useMemo(() => {
    if (!results.length) return 0;
    return new Set(results.map(r => r.product_code)).size;
  }, [results]);

  // Common prefix length across all group codes (for highlighting differences)
  const commonPrefixLen = useMemo(() => {
    if (!groupedResults || groupedResults.length < 2) return 0;
    const codes = groupedResults.map(([code]) => code);
    return findCommonPrefix(codes);
  }, [groupedResults]);
  const countDisplay = getResultCountDisplay(totalEstimate, searchInfo, results.length);

  return (
    <div ref={rootRef} className="flex flex-col h-full min-h-0">
      {/* Toolbar — fixed at top, never scrolls */}
      <div className="flex-shrink-0 flex items-center justify-between gap-2 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs text-text-secondary truncate" title={countDisplay.title}>
            {isCompact
              ? <strong className="text-text-primary tabular-nums">{countDisplay.value}</strong>
              : <strong className="text-text-primary">{countDisplay.compact}</strong>
            }
          </span>
          {searchInfo && (
            <span className="text-[10px] text-text-disabled tabular-nums">
              {searchInfo.execution_time_ms}мс
            </span>
          )}
          {onOpenPractice && (
            <button
              type="button"
              onClick={onOpenPractice}
              className={`inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-300 dark:hover:bg-emerald-900/40 ${
                isCompact ? 'p-1.5' : 'px-2 py-1'
              }`}
              title="Открыть практику оформлений по этому коду или префиксу"
            >
              <Layers className="w-3 h-3" />
              {!isCompact && <span>Практика оформлений</span>}
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Group by code toggle */}
          <div className="flex rounded-md border border-border-default overflow-hidden">
            <button
              onClick={() => setGroupByCode(false)}
              className={`flex items-center gap-1 px-2 py-1 text-[11px] transition-colors
                ${!groupByCode
                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 font-medium'
                  : 'text-text-disabled hover:bg-hover'
                }`}
              title="Плоский список"
              aria-label="Плоский список"
            >
              <List className="w-3 h-3" />
            </button>
            <button
              onClick={handleToggleGroup}
              className={`flex items-center gap-1 px-2 py-1 text-[11px] transition-colors border-l border-border-default
                ${groupByCode
                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 font-medium'
                  : 'text-text-disabled hover:bg-hover'
                }`}
              title="Группировка по коду"
              aria-label="Группировка по коду"
            >
              <Layers className="w-3 h-3" />
              {uniqueCodeCount > 1 && !isCompact && (
                <span className={`text-[9px] px-1 py-0 rounded-full ${groupByCode ? 'bg-blue-200 text-blue-800 dark:bg-blue-900/60 dark:text-blue-200' : 'bg-app text-text-muted'}`}>
                  {uniqueCodeCount}
                </span>
              )}
            </button>
          </div>
          {/* Group sort segmented control — only visible in grouped mode */}
          {groupByCode && (
            <div className="flex rounded-md border border-border-default overflow-hidden" role="group" aria-label="Сортировка групп">
              {[
                { key: 'relevance', Icon: Brain,     label: 'рел.',   tooltip: 'Сортировка: по релевантности' },
                { key: 'code',      Icon: Hash,      label: 'код',    tooltip: 'Сортировка: по коду' },
                { key: 'count',     Icon: BarChart3, label: 'кол-во', tooltip: 'Сортировка: по количеству' },
              ].map(({ key, Icon, label, tooltip }, i) => {
                const active = groupSort === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setGroupSort(key)}
                    aria-pressed={active}
                    title={tooltip}
                    aria-label={tooltip}
                    className={`flex items-center gap-1 px-2 py-1 text-[11px] transition-colors
                      ${i > 0 ? 'border-l border-border-default' : ''}
                      ${active
                        ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 font-medium'
                        : 'text-text-disabled hover:bg-hover'
                      }`}
                  >
                    <Icon className="w-3 h-3" />
                    {!isCompact && <span className="text-[10px]">{label}</span>}
                  </button>
                );
              })}
            </div>
          )}
          {!isCompact && (
            <div className="relative">
              <button
                onClick={() => setShowColMenu(!showColMenu)}
                className="p-1.5 rounded hover:bg-hover text-text-disabled transition-colors"
                title="Колонки"
                aria-label="Видимые колонки"
              >
                <Columns3 className="w-3.5 h-3.5" />
              </button>
              {showColMenu && (
                <div className="absolute right-0 top-full mt-1 bg-elevated border border-border-default rounded-lg shadow-lg z-40 p-1.5 min-w-[160px]">
                  {COLUMNS.map(c => (
                    <label key={c.key} className="flex items-center gap-2 px-2 py-1 text-xs text-text-secondary hover:bg-hover rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={visibleCols.has(c.key)}
                        onChange={() => toggleCol(c.key)}
                        className="rounded border-border-strong w-3 h-3"
                      />
                      {c.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          <button
            onClick={exportCsv}
            className={`inline-flex items-center gap-1 text-[11px] text-text-muted border border-border-default rounded hover:bg-hover transition-colors ${
              isCompact ? 'p-1.5' : 'px-2 py-1'
            }`}
            title="Экспорт CSV"
            aria-label="Экспорт CSV"
          >
            <Download className="w-3 h-3" />
            {!isCompact && <span>CSV</span>}
          </button>
        </div>
      </div>

      {/* Scrollable area — table or compact cards + pagination */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden rounded-lg border border-border-default">
        {isCompact ? (
          <div className="divide-y divide-border-default">
            {groupByCode && groupedResults ? (
              groupedResults.map(([code, rows], groupIdx) => {
                const isExpanded = groupIdx === 0
                  ? !expandedGroups.has(code)
                  : expandedGroups.has(code);
                return (
                  <React.Fragment key={code}>
                    <button
                      type="button"
                      onClick={() => toggleGroup(code)}
                      className="w-full flex items-center gap-2 bg-app hover:bg-hover px-3 py-2 text-left transition-colors"
                    >
                      {isExpanded
                        ? <ChevronDown className="w-3.5 h-3.5 text-text-disabled flex-shrink-0" />
                        : <ChevronRight className="w-3.5 h-3.5 text-text-disabled flex-shrink-0" />
                      }
                      <HSCode code={code} size="sm" commonPrefixLen={commonPrefixLen} showCopyAlways />
                      <span className="text-[10px] bg-app text-text-muted border border-border-default px-1.5 py-0.5 rounded-full font-medium">
                        {rows.length}
                      </span>
                      <span className="text-[10px] text-text-disabled ml-auto truncate">
                        {[...new Set(rows.map(r => r.country_of_origin).filter(Boolean))].slice(0, 4).join(', ')}
                      </span>
                    </button>
                    {isExpanded && rows.map((row, i) => (
                      <CompactRow
                        key={row.original_id || `${code}-${i}`}
                        row={row}
                        isSelected={selectedId != null && row.original_id === selectedId}
                        onRowClick={onRowClick}
                        searchQuery={searchQuery}
                      />
                    ))}
                  </React.Fragment>
                );
              })
            ) : (
              sortedResults.map((row, i) => (
                <CompactRow
                  key={row.original_id || i}
                  row={row}
                  isSelected={selectedId != null && row.original_id === selectedId}
                  onRowClick={onRowClick}
                  searchQuery={searchQuery}
                />
              ))
            )}
          </div>
        ) : (
        <table className="w-full divide-y divide-border-default table-fixed">
          <thead className="bg-app sticky top-0 z-10">
            <tr>
              {displayCols.map(col => (
                <th
                  key={col.key}
                  onClick={() => col.sortable && handleSort(col.key)}
                  className={`
                    px-2 py-2 text-left text-[10px] font-semibold text-text-muted uppercase tracking-wider bg-app
                    ${col.width} ${col.sortable ? 'cursor-pointer hover:bg-hover select-none' : ''}
                  `}
                  style={col.flex ? { width: 'auto' } : undefined}
                >
                  <div className="flex items-center gap-0.5">
                    {col.label}
                    {col.sortable && (
                      sortKey === col.key
                        ? (sortDir === 'asc' ? <ArrowUp className="w-2.5 h-2.5" /> : <ArrowDown className="w-2.5 h-2.5" />)
                        : <ArrowUpDown className="w-2.5 h-2.5 opacity-20" />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-surface divide-y divide-border-default">
            {groupByCode && groupedResults ? (
              /* === GROUPED MODE === */
              groupedResults.map(([code, rows], groupIdx) => {
                // First group auto-expanded, others need explicit expand
                const isExpanded = groupIdx === 0
                  ? !expandedGroups.has(code) // first group: expanded unless explicitly collapsed
                  : expandedGroups.has(code);  // other groups: collapsed unless explicitly expanded
                const colSpan = displayCols.length;

                return (
                  <React.Fragment key={code}>
                    {/* Group header */}
                    <tr
                      onClick={() => toggleGroup(code)}
                      className="cursor-pointer bg-app hover:bg-hover transition-colors border-b border-border-default"
                    >
                      <td colSpan={colSpan} className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {isExpanded
                            ? <ChevronDown className="w-3.5 h-3.5 text-text-disabled flex-shrink-0" />
                            : <ChevronRight className="w-3.5 h-3.5 text-text-disabled flex-shrink-0" />
                          }
                          <HSCode
                            code={code}
                            size="sm"
                            commonPrefixLen={commonPrefixLen}
                            showCopyAlways
                          />
                          <span className="text-[10px] bg-app text-text-muted border border-border-default px-1.5 py-0.5 rounded-full font-medium">
                            {rows.length}
                          </span>
                          {/* Show countries summary */}
                          <span className="text-[10px] text-text-disabled ml-1">
                            {[...new Set(rows.map(r => r.country_of_origin).filter(Boolean))].slice(0, 4).join(', ')}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {/* Group rows (only when expanded) */}
                    {isExpanded && rows.map((row, i) => (
                      <DataRow
                        key={row.original_id || `${code}-${i}`}
                        row={row}
                        activeCols={displayCols}
                        isSelected={selectedId != null && row.original_id === selectedId}
                        onRowClick={onRowClick}
                        searchQuery={searchQuery}
                      />
                    ))}
                  </React.Fragment>
                );
              })
            ) : (
              /* === FLAT MODE === */
              sortedResults.map((row, i) => (
                <DataRow
                  key={row.original_id || i}
                  row={row}
                  activeCols={displayCols}
                  isSelected={selectedId != null && row.original_id === selectedId}
                  onRowClick={onRowClick}
                  searchQuery={searchQuery}
                />
              ))
            )}
          </tbody>
        </table>
        )}
        {/* Pagination — inside scrollable area, at the bottom */}
        <Pagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalEstimate={totalEstimate}
          currentCount={results.length}
          searchInfo={searchInfo}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
}
